import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import { WorkOrderQueueItem, ProductDetails, CutSheetItem, BomItem, InventoryDrop, CreateDropRequest, DropCalculation, FabricSettings, PositioningData } from '../services/api';
import { Card, Button, StatusBadge, LoadingPage } from '../components/Layout';
import WorkCardModal from '../components/WorkCardModal';
import api from '../services/api';
import { api as apiService } from '../services/api';
import { sendToMobiPrint } from '../services/printer';

// Minimum usable drop length in inches
const MIN_USABLE_DROP = 84;

// Stock lengths for extrusions (in inches)
const STOCK_LENGTHS: Record<string, number> = {
  rollerTube: 288,     // 24' sticks
  housingMain: 288,    // 24' sticks
  housingCover: 288,   // 24' sticks
  weightBar: 288,      // 24' sticks
  vTrack: 324,         // 27' sticks
  uChannel: 324,       // 27' sticks
  kederGuide: 324,     // 27' sticks
  screwCover: 324,     // 27' sticks
};

// Convert decimal inches to fractional format for tape measures
function toFraction(decimal: number): string {
  const whole = Math.floor(decimal);
  const fraction = decimal - whole;

  // Common tape measure fractions (1/16 precision)
  const sixteenths = Math.round(fraction * 16);

  if (sixteenths === 0) {
    return `${whole}`;
  }
  if (sixteenths === 16) {
    return `${whole + 1}`;
  }

  // Reduce the fraction
  let num = sixteenths;
  let denom = 16;

  while (num % 2 === 0 && denom > 1) {
    num = num / 2;
    denom = denom / 2;
  }

  if (whole === 0) {
    return `${num}/${denom}`;
  }
  return `${whole} ${num}/${denom}`;
}

interface WorkOrderDetail extends WorkOrderQueueItem {
  product: ProductDetails;
  stepHistory: {
    step: string;
    workstationCode?: string;
    workstationId?: string;
    status: 'completed' | 'in_progress' | 'pending' | 'skipped';
    completedAt?: string;
    completedBy?: string;
  }[];
  // Additional fields for comprehensive display
  widthTop?: number;
  widthMiddle?: number;
  widthBottom?: number;
  heightLeft?: number;
  heightCenter?: number;
  heightRight?: number;
  screenLocation?: string;
  windowPanel?: boolean;
  windowOrientation?: string;
  windowCount?: number;
  windowWidth?: number;
  windowHeight?: number;
  topBorderInches?: number;
  sideBorderInches?: number;
  bottomBorderInches?: number;
}

// Label formatting helpers
const formatInstallationType = (value: string | undefined): string => {
  if (!value) return 'Wall';
  const map: Record<string, string> = {
    'face_of_header': 'Wall',
    'face of_header': 'Wall',
    'wall': 'Wall',
    'under_header': 'Ceiling',
    'ceiling': 'Ceiling',
    'recessed': 'Recessed (Pocket)',
    'recessed_pocket': 'Recessed (Pocket)',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatTrackMount = (value: string | undefined): string => {
  if (!value) return 'Inside Mount';
  const map: Record<string, string> = {
    'inside': 'Inside Mount',
    'inside_mount': 'Inside Mount',
    'outside': 'Outside Mount',
    'outside_mount': 'Outside Mount',
    'face': 'Face Mount',
    'face_mount': 'Face Mount',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatUChannel = (value: string | undefined): string => {
  if (!value) return 'No U-Channel';
  const map: Record<string, string> = {
    'none': 'No U-Channel',
    'no_u_channel': 'No U-Channel',
    'both': 'Both Sides',
    'both_sides': 'Both Sides',
    'two_sides': 'Both Sides',
    'left': 'Left Side Only',
    'left_side': 'Left Side Only',
    'left_only': 'Left Side Only',
    'right': 'Right Side Only',
    'right_side': 'Right Side Only',
    'right_only': 'Right Side Only',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatFabricType = (value: string | undefined): string => {
  if (!value) return value || '';
  const map: Record<string, string> = {
    'solar94': 'Solar',
    'solar_94': 'Solar',
    'solar': 'Solar',
    'solar99': 'Premium Solar',
    'solar_99': 'Premium Solar',
    'premium_solar': 'Premium Solar',
    'omega_pro_3': 'Omega Pro 3%',
    'omega_pro_5': 'Omega Pro 5%',
    'omega_pro_8': 'Omega Pro 8%',
    'omega_pro_10': 'Omega Pro 10%',
    'omega3': 'Omega Pro 3%',
    'omega5': 'Omega Pro 5%',
    'clear_vinyl_20': 'Clear Vinyl (20 Gauge)',
    'clear_vinyl': 'Clear Vinyl',
    '20 gauge clear vinyl': 'Clear Vinyl (20 Gauge)',
    'cvw': 'Clear Vinyl',
    '502_vinyl': '502 Vinyl',
    'insect_screen': 'Insect Screen',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Generic formatter for DB snake_case values
const formatDbValue = (value: string | undefined): string => {
  if (!value) return '';
  return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatColor = (value: string | undefined): string => {
  if (!value) return '';
  const map: Record<string, string> = {
    'black': 'Black',
    'white': 'White',
    'bronze': 'Bronze',
    'tan': 'Tan',
    'mill_finish': 'Mill Finish',
    'custom': 'Custom',
    'matte_black': 'Matte Black',
    'matte black': 'Matte Black',
  };
  return map[value.toLowerCase()] || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatBrushSize = (value: string | undefined): string => {
  if (!value) return '1"';
  const map: Record<string, string> = {
    '1': '1"',
    '1_inch': '1"',
    '1"': '1"',
    '1.5': '1.5"',
    '1_5': '1.5"',
    '1.5_inch': '1.5"',
    '2': '2"',
    '2_inch': '2"',
    '2"': '2"',
  };
  return map[value.toLowerCase()] || value;
};

// Helper function to fetch and open an installer sheet URL with auth
const fetchInstallerSheet = async (url: string): Promise<void> => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    alert('Please log in to view installer sheets.');
    return;
  }

  const response = await fetch(url, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (response.status === 401) {
    alert('Session expired. Please log in again.');
    return;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Installer sheet error:', response.status, errorText);
    throw new Error(`Failed to fetch installer sheet: ${response.status}`);
  }

  const html = await response.text();
  const blob = new Blob([html], { type: 'text/html' });
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
};

// Open installer sheet for a single unit
const openInstallerSheet = async (orderId: string, itemNumber: number) => {
  try {
    await fetchInstallerSheet(`/api/bom/installer-sheet/${orderId}/${itemNumber}`);
  } catch (error) {
    console.error('Error opening installer sheet:', error);
    alert('Failed to load installer sheet. Please try again.');
  }
};

// Open all installer sheets for the entire order (combined print)
const openAllInstallerSheets = async (orderId: string) => {
  try {
    await fetchInstallerSheet(`/api/bom/installer-sheets/${orderId}`);
  } catch (error) {
    console.error('Error opening all installer sheets:', error);
    alert('Failed to load installer sheets. Please try again.');
  }
};

export function WorkOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState<WorkOrderDetail | null>(null);
  const [cutSheet, setCutSheet] = useState<CutSheetItem[]>([]);
  const [bomItems, setBomItems] = useState<BomItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'cutsheet' | 'bom'>('details');
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [allUnits, setAllUnits] = useState<any[]>([]);
  const [currentUnitIndex, setCurrentUnitIndex] = useState(0);
  const printRef = useRef<HTMLDivElement>(null);

  // Work-card modal — opens in-place instead of navigating to /work-cards
  const [cardModal, setCardModal] = useState<{ open: boolean; wsId: string; woId: string }>({
    open: false, wsId: '', woId: '',
  });
  const openCard = (wsId: string, woId: string) => setCardModal({ open: true, wsId, woId });
  const closeCard = () => {
    setCardModal({ open: false, wsId: '', woId: '' });
    // Refresh detail so workflow progress reflects newly-completed steps
    loadWorkOrder();
  };

  // Drop entry modal state
  const [showDropModal, setShowDropModal] = useState(false);
  const [dropLength, setDropLength] = useState('');
  const [dropPartType, setDropPartType] = useState('');
  const [dropPartNumber, setDropPartNumber] = useState('');
  const [dropNotes, setDropNotes] = useState('');
  const [createdDrop, setCreatedDrop] = useState<InventoryDrop | null>(null);
  const [showBinScanModal, setShowBinScanModal] = useState(false);
  const [binLocation, setBinLocation] = useState('');
  const [showDropLabelModal, setShowDropLabelModal] = useState(false);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  const [availableDrops, setAvailableDrops] = useState<InventoryDrop[]>([]);
  const [showAvailableDropsModal, setShowAvailableDropsModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_selectedExistingDrop, setSelectedExistingDrop] = useState<InventoryDrop | null>(null);

  // Fabric settings for cut/sealer stations
  const [fabricSettings, setFabricSettings] = useState<FabricSettings | null>(null);

  // Positioning data for parts with specific installation positions
  const [positioning, setPositioning] = useState<PositioningData | null>(null);

  // Get current workstation from localStorage (UUID) for BOM filtering
  const currentWorkstation = localStorage.getItem('selectedWorkstation') || '';

  // Detect station type from work order data (not localStorage UUID)
  const wsCode = (workOrder as any)?.workstationCode?.toUpperCase() || '';
  const wsType = (workOrder as any)?.workstationType || '';
  const isCutStation = wsType === 'cutting' || ['SAW1', 'SAW2', 'SAW3', 'CUTTABLE'].includes(wsCode);
  const isFabricStation = ['CUTTABLE', 'SEALER', 'CORNERPATCH'].includes(wsCode) || wsCode.includes('FAB');
  const isPartsOrder = (workOrder as any)?.productType === 'part';
  const partSpecs = (workOrder as any)?.specs || {};

  useEffect(() => {
    loadWorkOrder();
  }, [id]);

  // Load fabric settings when work order is loaded and station is fabric-related
  useEffect(() => {
    const loadFabricSettings = async () => {
      if (workOrder && isFabricStation && workOrder.screenMaterial) {
        try {
          const settings = await apiService.getFabricSettings(workOrder.screenMaterial);
          setFabricSettings(settings);
        } catch (e) {
          console.log('Could not load fabric settings:', e);
        }
      }
    };
    loadFabricSettings();
  }, [workOrder, isFabricStation]);

  const loadWorkOrder = async () => {
    try {
      // Fetch real work order from API - no mock fallback
      const workOrderData = await api.getWorkOrderDetail(id || '');
      if (!workOrderData) {
        console.error('Work order not found');
        setLoading(false);
        return;
      }
      setWorkOrder(workOrderData);

      // Load all units in this order for the unit toggle
      if (workOrderData.orderId) {
        try {
          const units = await api.getWorkOrdersByOrder(workOrderData.orderId);
          setAllUnits(units);
          const idx = units.findIndex((u: any) => u.work_order_id === workOrderData.workOrderId || u.item_number === workOrderData.itemNumber);
          setCurrentUnitIndex(idx >= 0 ? idx : 0);
        } catch { setAllUnits([]); }
      }

      // Load cut sheet data
      const orderId = workOrderData.orderId;
      if (orderId) {
        try {
          const cutSheetData = await api.getCutSheet(orderId);
          setCutSheet(cutSheetData);
        } catch (e) {
          console.log('Could not load cut sheet:', e);
          setCutSheet([]);
        }

        // Load BOM items filtered by current workstation
        try {
          const bomData = await api.getBomItems(orderId, currentWorkstation);
          setBomItems(bomData);
        } catch (e) {
          console.log('Could not load BOM items:', e);
          setBomItems([]);
        }

        // Load positioning data for parts with specific installation positions
        try {
          const positioningData = await api.getPositioning(orderId);
          setPositioning(positioningData);
        } catch {
          setPositioning(null);
        }
      }
    } catch (error) {
      console.error('Failed to load work order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLabel = async (labelType?: string) => {
    if (!labelType) {
      setShowLabelPicker(true);
      return;
    }
    setShowLabelPicker(false);
    setActionLoading('print');
    try {
      if (workOrder) {
        const result = await apiService.queueAndPrintLabel(workOrder.id, labelType);
        if (!result.success) {
          console.error('Print failed:', result.message);
        }
      }
    } catch (error) {
      console.error('Failed to print label:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePrintCutSheet = () => {
    setIsPrintMode(true);
    setTimeout(() => {
      window.print();
      setIsPrintMode(false);
    }, 100);
  };

  const handleComplete = async () => {
    // For cut stations, auto-calculate drop from BOM and show the drop entry modal
    if (isCutStation && !createdDrop) {
      try {
        setActionLoading('calc');
        const calcs = await apiService.calculateDropInfo(workOrder!.id);
        if (calcs.length > 0) {
          const calc = calcs[0]; // Primary cut item
          setDropLength(String(calc.calculatedDropLength));
          setDropPartType(calc.partType);
          setDropPartNumber(calc.partNumber);
        }
      } catch (e) {
        console.log('Could not auto-calculate drop:', e);
      } finally {
        setActionLoading(null);
      }
      setShowDropModal(true);
      return;
    }

    await finishWorkOrder();
  };

  // Function to actually complete the work order after drop handling
  const finishWorkOrder = async () => {
    setActionLoading('complete');
    try {
      if (workOrder) {
        // Complete the work order using UUID
        await api.completeWorkOrder(workOrder.id);

        // Consume BOM items for this order
        try {
          await api.consumeBomItems(workOrder.orderId);
        } catch (e) {
          console.warn('Could not consume BOM items:', e);
        }
      }
      navigate('/queue');
    } catch (error) {
      console.error('Failed to complete:', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Handle creating a drop (leftover material)
  const handleCreateDrop = async () => {
    const length = parseFloat(dropLength);
    if (isNaN(length) || length <= 0) {
      alert('Please enter a valid length in inches');
      return;
    }

    setActionLoading('drop');
    try {
      const dropData: CreateDropRequest = {
        partNumber: dropPartNumber || `${dropPartType}-DROP`,
        partType: dropPartType || 'extrusion',
        lengthInches: length,
        workOrderId: workOrder?.id,
        orderNumber: workOrder?.orderNumber,
        notes: dropNotes || undefined,
      };

      const drop = await api.createDrop(dropData);
      setCreatedDrop(drop);
      setShowDropModal(false);

      // If usable drop (>=84"), show bin scan modal to store it
      if (drop.status === 'available') {
        setShowDropLabelModal(true);
      } else {
        // Waste - just complete the work order
        await finishWorkOrder();
      }
    } catch (error) {
      console.error('Failed to create drop:', error);
      alert('Failed to save drop. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // Skip drop entry (no leftover)
  const handleSkipDrop = async () => {
    setShowDropModal(false);
    await finishWorkOrder();
  };

  // Handle bin location entry for drop
  const handleSaveBinLocation = async (printLabel = false) => {
    if (!createdDrop || !binLocation.trim()) {
      alert('Please enter a bin location');
      return;
    }

    setActionLoading('bin');
    try {
      const trimmedBin = binLocation.trim().toUpperCase();
      await api.updateDropLocation(createdDrop.id, trimmedBin);

      // Print label with bin location if requested
      if (printLabel) {
        handlePrintDropLabel(trimmedBin);
      }

      setShowBinScanModal(false);
      await finishWorkOrder();
    } catch (error) {
      console.error('Failed to save bin location:', error);
      alert('Failed to save bin location. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // Print drop label directly to Zebra printer via local network
  const handlePrintDropLabel = async (binLoc?: string) => {
    if (!createdDrop) return;

    const binLocationDisplay = binLoc || createdDrop.binLocation || binLocation || 'NOT ASSIGNED';

    try {
      const result = await apiService.generateDropLabel({
        dropId: createdDrop.dropId,
        partType: createdDrop.partType,
        length: createdDrop.lengthInches || 0,
        partNumber: createdDrop.partNumber,
        binLocation: binLocationDisplay,
      });
      // Download .zpl file — Zebra app opens it and prints
      await sendToMobiPrint(result.zpl);
    } catch (err) {
      console.error('Failed to print drop label:', err);
      alert('Failed to generate drop label. Please try again.');
    }
  };

  // After printing label, show bin scan modal
  const handleAfterPrint = () => {
    setShowDropLabelModal(false);
    setShowBinScanModal(true);
  };

  // Load available drops for a part type when starting a cut
  const loadAvailableDrops = async (partType: string) => {
    try {
      const drops = await api.getAvailableDrops(partType);
      setAvailableDrops(drops);
      if (drops.length > 0) {
        setShowAvailableDropsModal(true);
      }
    } catch (error) {
      console.error('Failed to load available drops:', error);
    }
  };

  // Use an existing drop instead of new stock
  const handleUseExistingDrop = async (drop: InventoryDrop) => {
    if (!workOrder) return;

    setActionLoading('useDrop');
    try {
      await api.useDrop(drop.id, {
        workOrderId: workOrder.id,
        orderNumber: workOrder.orderNumber,
      });
      setSelectedExistingDrop(drop);
      setShowAvailableDropsModal(false);
      alert(`Using drop ${drop.dropId} (${drop.lengthInches}") from ${drop.binLocation || 'unknown location'}`);
    } catch (error) {
      console.error('Failed to use drop:', error);
      alert('Failed to mark drop as used. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (!workOrder) {
    return (
      <Card className="text-center py-12">
        <div className="text-gray-400 text-lg">Work order not found</div>
        <Button variant="primary" onClick={() => navigate('/queue')} className="mt-4">
          Back to Queue
        </Button>
      </Card>
    );
  }

  const { product } = workOrder;

  // Find the cut sheet entry matching this work order's unit (not always [0])
  const unitCutSheet = cutSheet.find(cs => cs.itemNumber === workOrder.itemNumber) || cutSheet[0] || null;

  // Print mode - simplified large font view for factory floor
  if (isPrintMode) {
    const printStyle = (
      <style>{`
          @media print {
            body { background: white !important; }
            .print-cut-sheet {
              background: white !important;
              color: black !important;
              font-size: 18pt !important;
            }
            .print-cut-sheet h1 { font-size: 28pt !important; }
            .print-cut-sheet h2 { font-size: 22pt !important; }
            .print-cut-sheet .cut-value { font-size: 32pt !important; font-weight: bold; }
            .print-cut-sheet table { width: 100%; border-collapse: collapse; }
            .print-cut-sheet th, .print-cut-sheet td {
              border: 2px solid black;
              padding: 12px;
              text-align: left;
            }
            .print-cut-sheet .spring-holes {
              font-size: 24pt !important;
              line-height: 1.8;
            }
          }
        `}</style>
    );

    // Parts orders: print a saw cut list — every length-based part in the order
    // on one sheet (part, color, cut length, qty, saw). No screen sections.
    if (isPartsOrder) {
      const partRows = (allUnits.length > 0 ? allUnits : [{ specs: partSpecs, workstation_code: workOrder.workstationCode }])
        .map((u: any) => ({ specs: (u.specs || {}), station: u.workstation_code || u.workstationCode }))
        .filter((r: any) => r.specs && (r.specs.partLength != null || r.specs.partName));
      return (
        <div ref={printRef} className="print-cut-sheet bg-white text-black p-8 min-h-screen">
          {printStyle}
          <div className="text-center mb-8 border-b-4 border-black pb-4">
            <h1 className="text-4xl font-bold">{workOrder.orderNumber} — Parts Cut Sheet</h1>
            <div className="text-2xl mt-2">{workOrder.sidemark}</div>
            <div className="text-xl mt-1">{workOrder.customerName}</div>
          </div>
          <table className="w-full mb-8">
            <thead>
              <tr className="bg-gray-200">
                <th className="text-left p-3 text-xl">Part</th>
                <th className="text-left p-3 text-xl">Color</th>
                <th className="text-right p-3 text-xl">Cut (W × H)</th>
                <th className="text-right p-3 text-xl">Qty</th>
                <th className="text-right p-3 text-lg">Station</th>
              </tr>
            </thead>
            <tbody>
              {partRows.map((r: any, idx: number) => (
                <tr key={idx} className={idx % 2 ? 'bg-gray-100' : ''}>
                  <td className="p-3 text-xl font-bold">{r.specs.partName || 'Part'}{r.specs.seam ? ' — SEAM (2 panels)' : ''}</td>
                  <td className="p-3 text-xl capitalize">{r.specs.partColor || '—'}</td>
                  <td className="p-3 text-right cut-value">
                    {(r.specs.partLength ?? r.specs.fabricCut) ?? '—'}"{r.specs.fabricHeight != null ? ` × ${r.specs.fabricHeight}"` : ''}
                  </td>
                  <td className="p-3 text-right cut-value">{r.specs.partQuantity ?? 1}</td>
                  <td className="p-3 text-right text-lg">{r.station || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-lg italic">Cut lengths are finished sizes — do not add allowance.</div>
        </div>
      );
    }

    return (
      <div ref={printRef} className="print-cut-sheet bg-white text-black p-8 min-h-screen">
        {printStyle}

        <div className="text-center mb-8 border-b-4 border-black pb-4">
          <h1 className="text-4xl font-bold">{workOrder.orderNumber} - Unit {workOrder.itemNumber}</h1>
          <div className="text-2xl mt-2">{workOrder.sidemark}</div>
          <div className="text-xl mt-1">{workOrder.customerName}</div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h2 className="text-2xl font-bold border-b-2 border-black pb-2 mb-4">System Dimensions</h2>
            <div className="space-y-2 text-xl">
              <div><strong>Width:</strong> <span className="cut-value">{unitCutSheet?.systemWidth || workOrder.width}"</span></div>
              <div><strong>Height:</strong> <span className="cut-value">{unitCutSheet?.systemHeight || workOrder.height}"</span></div>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold border-b-2 border-black pb-2 mb-4">Material</h2>
            <div className="space-y-2 text-xl">
              <div><strong>Type:</strong> {formatFabricType(workOrder.screenMaterial)}</div>
              <div><strong>Color:</strong> {workOrder.screenColor}</div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold border-b-2 border-black pb-2 mb-4">Component Cut Sizes</h2>
        <table className="w-full mb-8">
          <thead>
            <tr className="bg-gray-200">
              <th className="text-left p-3 text-xl">Component</th>
              <th className="text-right p-3 text-xl">Cut Length (Fraction)</th>
              <th className="text-right p-3 text-xl">Cut Length (Decimal)</th>
              <th className="text-right p-3 text-sm">Stock</th>
            </tr>
          </thead>
          <tbody>
            {unitCutSheet && (
              <>
                <tr><td className="p-3 text-xl font-bold">Roller Tube</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.rollerTube)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.rollerTube.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">24'</td></tr>
                <tr className="bg-gray-100"><td className="p-3 text-xl font-bold">Weight Bar</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.weightBar)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.weightBar.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">24'</td></tr>
                <tr><td className="p-3 text-xl">Housing Main</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.housingMain)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.housingMain.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">24'</td></tr>
                <tr className="bg-gray-100"><td className="p-3 text-xl">Housing Cover</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.housingCover)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.housingCover.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">24'</td></tr>
                <tr><td className="p-3 text-xl">V-Tracks (x2)</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.vTrack)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.vTrack.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">27'</td></tr>
                {unitCutSheet.cutSizes.uChannel && (
                  <tr className="bg-gray-100"><td className="p-3 text-xl">U-Channel (x2)</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.uChannel)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.uChannel.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">27'</td></tr>
                )}
                {unitCutSheet.cutSizes.kederGuide && (
                  <tr><td className="p-3 text-xl">Keder Guide (x2)</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.kederGuide)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.kederGuide.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">27'</td></tr>
                )}
                {unitCutSheet.cutSizes.screwCover && (
                  <tr className="bg-gray-100"><td className="p-3 text-xl">Screw Cover</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.screwCover)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.screwCover.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">27'</td></tr>
                )}
                <tr style={{borderTop: '3px solid black'}}><td className="p-3 text-xl font-bold">Fabric Width</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.fabricWidth)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.fabricWidth.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">-</td></tr>
                <tr className="bg-gray-100"><td className="p-3 text-xl font-bold">Fabric Height</td><td className="p-3 text-right cut-value">{toFraction(unitCutSheet.cutSizes.fabricHeight)}"</td><td className="p-3 text-right text-lg">{unitCutSheet.cutSizes.fabricHeight.toFixed(4).replace(/\.?0+$/, '')}"</td><td className="p-3 text-right text-sm text-gray-600">-</td></tr>
              </>
            )}
          </tbody>
        </table>

        <h2 className="text-2xl font-bold border-b-2 border-black pb-2 mb-4">Spring Hole Positions</h2>
        {unitCutSheet && (
          <div className="mb-8">
            <div className="text-xl mb-4">
              <strong>Total Holes:</strong> {unitCutSheet.springHoles.count} |
              <strong className="ml-4">Spacing:</strong> {unitCutSheet.springHoles.spacing.toFixed(2)}"
            </div>
            <div className="spring-holes grid grid-cols-2 gap-4 text-2xl font-mono bg-gray-100 p-4 rounded">
              {unitCutSheet.springHoles.positionsFormatted.map((pos, idx) => (
                <div key={idx} className="py-1">{pos}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <Card>
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-yellow-400 font-semibold text-lg">{workOrder.orderNumber}</span>
              {workOrder.isRush && (
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded font-bold">RUSH</span>
              )}
              {workOrder.isLate && (
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">LATE</span>
              )}
            </div>
            {allUnits.length > 1 ? (
              <div className="flex items-center gap-2 mt-1">
                <button
                  onClick={() => {
                    if (currentUnitIndex > 0) {
                      const prev = allUnits[currentUnitIndex - 1];
                      navigate(`/work/${prev.work_order_id}`);
                    }
                  }}
                  disabled={currentUnitIndex <= 0}
                  className="px-2 py-0.5 bg-yellow-400 text-black rounded text-sm font-bold disabled:opacity-30"
                >&lt;</button>
                <span className="text-sm text-yellow-400 font-medium">{isPartsOrder ? 'Part' : 'Screen'} {currentUnitIndex + 1} of {allUnits.length}</span>
                <button
                  onClick={() => {
                    if (currentUnitIndex < allUnits.length - 1) {
                      const next = allUnits[currentUnitIndex + 1];
                      navigate(`/work/${next.work_order_id}`);
                    }
                  }}
                  disabled={currentUnitIndex >= allUnits.length - 1}
                  className="px-2 py-0.5 bg-yellow-400 text-black rounded text-sm font-bold disabled:opacity-30"
                >&gt;</button>
              </div>
            ) : (
              <div className="text-sm text-gray-400">Unit {workOrder.itemNumber}</div>
            )}
          </div>
          <StatusBadge status={workOrder.status} />
        </div>

        <div className="mb-3">
          <div className="text-xl font-semibold text-white">{workOrder.productName}</div>
          <div className="text-gray-400">{workOrder.customerName}</div>
          <div className="text-gray-500">Sidemark: {workOrder.sidemark}</div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">{isPartsOrder ? 'Cut:' : 'Dimensions:'}</span>
            <span className="text-white ml-2 font-medium">
              {isPartsOrder
                ? (partSpecs.fabricHeight != null
                    ? `${partSpecs.fabricCut ?? '—'}" W × ${partSpecs.fabricHeight}" H × ${partSpecs.partQuantity ?? 1}`
                    : `${(partSpecs.partLength ?? partSpecs.fabricCut) ?? '—'}" × ${partSpecs.partQuantity ?? 1}`)
                : `${workOrder.width}" x ${workOrder.height}"`}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Due Date:</span>
            <span className={clsx('ml-2 font-medium', workOrder.isLate ? 'text-red-400' : 'text-white')}>
              {workOrder.dueDate && !isNaN(new Date(workOrder.dueDate).getTime())
                ? format(new Date(workOrder.dueDate), 'MMM d, yyyy')
                : 'Not set'}
            </span>
          </div>
        </div>
        {workOrder.screenLocation && (
          <div className="mt-3 p-2 bg-blue-900/30 border border-blue-500/50 rounded">
            <div className="text-xs text-blue-400 font-semibold">SCREEN LOCATION</div>
            <div className="text-white font-medium">{workOrder.screenLocation}</div>
          </div>
        )}
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('details')}
          className={clsx(
            'flex-1 py-4 px-4 rounded-lg text-lg font-medium transition-colors touch-target',
            activeTab === 'details'
              ? 'bg-yellow-400 text-black'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          )}
        >
          Details
        </button>
        <button
          onClick={() => setActiveTab('cutsheet')}
          className={clsx(
            'flex-1 py-4 px-4 rounded-lg text-lg font-medium transition-colors touch-target',
            activeTab === 'cutsheet'
              ? 'bg-yellow-400 text-black'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          )}
        >
          Cut Sheet
        </button>
        <button
          onClick={() => setActiveTab('bom')}
          className={clsx(
            'flex-1 py-4 px-4 rounded-lg text-lg font-medium transition-colors touch-target',
            activeTab === 'bom'
              ? 'bg-yellow-400 text-black'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          )}
        >
          Parts
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'cutsheet' && (
        <>
          {/* Action Buttons */}
          <div className="flex justify-between gap-3">
            {isCutStation && (
              <Button
                variant="primary"
                onClick={() => {
                  // Get a part type from the first BOM item or cut sheet
                  const partType = bomItems[0]?.componentType || 'roller_tube';
                  loadAvailableDrops(partType);
                }}
                className="touch-target"
              >
                Check Available Drops
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handlePrintCutSheet}
              className="touch-target ml-auto"
            >
              Print Cut Sheet
            </Button>
          </div>

          {/* Parts orders: simple cut list — part, color, length, qty.
              Screen-specific sections (component cut sizes, spring holes,
              octagonal positioning) do NOT apply to a cut-to-length part. */}
          {isPartsOrder && (
            <Card className="border-2 border-yellow-400/50">
              <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Cut List
              </h3>
              <div className="bg-gray-800 rounded-xl p-5">
                <div className="text-2xl font-bold text-white mb-4">{partSpecs.partName || 'Part'}</div>
                <div className={`grid ${partSpecs.fabricHeight != null ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
                  <div className="bg-yellow-900/30 rounded-lg p-4 text-center">
                    <div className="text-4xl font-bold text-yellow-400">{(partSpecs.partLength ?? partSpecs.fabricCut) ?? '—'}"</div>
                    <div className="text-sm text-gray-400 mt-1">{partSpecs.fabricCut != null ? 'Fabric Width' : 'Cut Length'}</div>
                  </div>
                  {partSpecs.fabricHeight != null && (
                    <div className="bg-yellow-900/30 rounded-lg p-4 text-center">
                      <div className="text-4xl font-bold text-yellow-400">{partSpecs.fabricHeight}"</div>
                      <div className="text-sm text-gray-400 mt-1">Fabric Height</div>
                    </div>
                  )}
                  <div className="bg-yellow-900/30 rounded-lg p-4 text-center">
                    <div className="text-4xl font-bold text-yellow-400">{partSpecs.partQuantity ?? 1}</div>
                    <div className="text-sm text-gray-400 mt-1">Pieces</div>
                  </div>
                </div>
                {partSpecs.partColor && (
                  <div className="mt-4 text-lg text-gray-300">Color: <span className="text-white font-semibold capitalize">{partSpecs.partColor}</span></div>
                )}
                {partSpecs.seam && (
                  <div className="mt-3 inline-block bg-red-900 border border-red-500 rounded px-3 py-1 text-sm font-bold text-red-200">SEAM REQUIRED — 2 panels</div>
                )}
                <div className="mt-3 text-xs text-gray-500">{partSpecs.fabricCut != null ? 'Fabric cut width already includes allowance.' : 'Length is the finished cut size — do not add allowance.'}</div>
              </div>
            </Card>
          )}

          {/* Cut Sheet Section (screens only) */}
          {!isPartsOrder && (
          <Card className="border-2 border-yellow-400/50">
            <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Component Cut Sizes
            </h3>
            {unitCutSheet && (
              <div className="space-y-2">
                {/* Horizontal extrusions */}
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Horizontal Components</div>
                <CutRow label="Roller Tube" value={unitCutSheet.cutSizes.rollerTube} unit="inches" highlight isExtrusion stockLength={STOCK_LENGTHS.rollerTube} />
                <CutRow label="Weight Bar" value={unitCutSheet.cutSizes.weightBar} unit="inches" highlight isExtrusion stockLength={STOCK_LENGTHS.weightBar} />
                {unitCutSheet.cutSizes.housingMain != null && (
                  <CutRow label="Housing Main" value={unitCutSheet.cutSizes.housingMain} unit="inches" isExtrusion stockLength={STOCK_LENGTHS.housingMain} />
                )}
                {unitCutSheet.cutSizes.housingCover != null && (
                  <CutRow label="Housing Cover" value={unitCutSheet.cutSizes.housingCover} unit="inches" isExtrusion stockLength={STOCK_LENGTHS.housingCover} />
                )}

                {/* Vertical extrusions */}
                <div className="text-xs text-gray-500 uppercase tracking-wide mt-4 mb-1">Vertical Components</div>
                <CutRow label="V-Tracks (x2)" value={unitCutSheet.cutSizes.vTrack} unit="inches" isExtrusion stockLength={STOCK_LENGTHS.vTrack} />
                {unitCutSheet.cutSizes.uChannel && (
                  <CutRow label="U-Channel (x2)" value={unitCutSheet.cutSizes.uChannel} unit="inches" isExtrusion stockLength={STOCK_LENGTHS.uChannel} />
                )}
                {unitCutSheet.cutSizes.kederGuide && (
                  <CutRow label="Keder Guide (x2)" value={unitCutSheet.cutSizes.kederGuide} unit="inches" isExtrusion stockLength={STOCK_LENGTHS.kederGuide} />
                )}
                {unitCutSheet.cutSizes.screwCover && (
                  <CutRow label="Screw Cover" value={unitCutSheet.cutSizes.screwCover} unit="inches" isExtrusion stockLength={STOCK_LENGTHS.screwCover} />
                )}

                {/* Fabric measurements */}
                <div className="border-t border-gray-700 pt-3 mt-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fabric</div>
                  <CutRow label="Fabric Width" value={unitCutSheet.cutSizes.fabricWidth} unit="inches" highlight />
                  <CutRow label="Fabric Height" value={unitCutSheet.cutSizes.fabricHeight} unit="inches" highlight />
                </div>
              </div>
            )}

            {/* Drop Allocations */}
            {unitCutSheet?.dropAllocations && unitCutSheet.dropAllocations.length > 0 && (
              <div className="mt-4 bg-green-900/30 border-2 border-green-500 rounded-xl p-4">
                <div className="text-green-400 text-sm font-bold mb-2 uppercase">Use Existing Drops — Do Not Cut From New Stock</div>
                {unitCutSheet.dropAllocations.map((drop: any, idx: number) => (
                  <div key={idx} className="bg-green-900/50 rounded-lg p-3 mb-2 last:mb-0">
                    <div className="text-green-100 font-bold text-lg">{drop.dropId}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span className="text-green-200 text-sm">{(drop.componentType || '').replace(/_/g, ' ')}</span>
                      <span className="text-green-200 text-sm font-medium">BIN: {drop.dropBinLocation || 'Check inventory'}</span>
                      <span className="text-green-200 text-sm">Drop: {drop.dropLength}"</span>
                      <span className="text-green-200 text-sm">Cut to: {drop.cutLength}"</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
          )}

          {/* Spring Holes Section (screens only) */}
          {!isPartsOrder && (
          <Card className="border-2 border-blue-400/50">
            <h3 className="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Spring Hole Positions
            </h3>
            {unitCutSheet && (
              <>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-blue-900/30 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-400">{unitCutSheet.springHoles.count}</div>
                    <div className="text-sm text-gray-400">Total Holes</div>
                  </div>
                  <div className="bg-blue-900/30 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-400">{unitCutSheet.springHoles.spacing.toFixed(2)}"</div>
                    <div className="text-sm text-gray-400">Spacing</div>
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-3 font-medium">Positions from Top</div>
                  <div className="grid grid-cols-2 gap-2">
                    {unitCutSheet.springHoles.positionsFormatted.map((pos, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-900 rounded px-4 py-3 text-lg font-mono text-white"
                      >
                        {pos}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </Card>
          )}

          {/* Part Positioning - for parts installed at specific positions */}
          {!isPartsOrder && positioning?.hasPositionedParts && positioning.parts.length > 0 && (
            <Card className="border-2 border-teal-400/50">
              <h3 className="text-xl font-bold text-teal-400 mb-4 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Part Positioning
              </h3>
              {positioning.factoryDeductionTotal > 0 && (
                <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3 mb-4">
                  <div className="text-sm text-yellow-300">
                    Factory Deduction Applied: <span className="font-bold">{positioning.factoryDeductionTotal}"</span>
                    <span className="text-yellow-400/80 ml-2">(positions adjusted outward)</span>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {positioning.parts.map((part, idx) => (
                  <div key={idx} className="bg-gray-800 rounded-lg p-4">
                    <div className="font-semibold text-white mb-2">{part.description}</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-teal-900/30 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-teal-400">{toFraction(part.effectiveMotorPosition)}"</div>
                        <div className="text-sm text-gray-400">Motor Side</div>
                        {part.effectiveMotorPosition !== part.motorSidePosition && (
                          <div className="text-xs text-gray-500 mt-1">
                            (base: {toFraction(part.motorSidePosition)}")
                          </div>
                        )}
                      </div>
                      <div className="bg-teal-900/30 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-teal-400">{toFraction(part.effectiveIdlerPosition)}"</div>
                        <div className="text-sm text-gray-400">Idler Side</div>
                        {part.effectiveIdlerPosition !== part.idlerSidePosition && (
                          <div className="text-xs text-gray-500 mt-1">
                            (base: {toFraction(part.idlerSidePosition)}")
                          </div>
                        )}
                      </div>
                    </div>
                    {part.notes && (
                      <div className="text-sm text-gray-400 mt-2 italic">{part.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Seam Info */}
          {unitCutSheet?.seamRequired && (
            <Card className="border-2 border-orange-400/50">
              <h3 className="text-xl font-bold text-orange-400 mb-4">Seam Required</h3>
              <div className="space-y-2">
                <DetailRow label="Panel 1 Height" value={`${unitCutSheet.seamDetails?.panel1Height}"`} highlight />
                <DetailRow label="Panel 2 Height" value={`${unitCutSheet.seamDetails?.panel2Height}"`} highlight />
                <DetailRow label="Total Height" value={`${unitCutSheet.seamDetails?.totalHeight}"`} />
              </div>
            </Card>
          )}

          {/* Fabric Settings (for Cut Table / Sealer stations) */}
          {isFabricStation && fabricSettings && (
            <Card className="border-2 border-purple-400/50">
              <h3 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                </svg>
                Fabric Settings - {fabricSettings.fabricType}
              </h3>

              {/* Weld Settings */}
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-purple-300 mb-3 border-b border-purple-500/30 pb-2">
                  Weld Settings
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-red-400">
                      {fabricSettings.weldTempHot ? `${fabricSettings.weldTempHot}s` : '--'}
                    </div>
                    <div className="text-sm text-gray-400">Hot Time</div>
                  </div>
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-400">
                      {fabricSettings.weldTempCold ? `${fabricSettings.weldTempCold}s` : '--'}
                    </div>
                    <div className="text-sm text-gray-400">Cold Time</div>
                  </div>
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-400">
                      {fabricSettings.weldSpeed ?? '--'}
                    </div>
                    <div className="text-sm text-gray-400">Speed</div>
                  </div>
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-400">
                      {fabricSettings.weldPressure ?? '--'}
                    </div>
                    <div className="text-sm text-gray-400">Pressure</div>
                  </div>
                </div>
                {fabricSettings.weldNotes && (
                  <div className="mt-3 p-3 bg-purple-900/20 rounded-lg text-sm text-purple-200">
                    <strong>Notes:</strong> {fabricSettings.weldNotes}
                  </div>
                )}
              </div>

              {/* Cut Table Settings */}
              <div>
                <h4 className="text-lg font-semibold text-purple-300 mb-3 border-b border-purple-500/30 pb-2">
                  Cut Table Settings
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-xl font-bold text-yellow-400">
                      {fabricSettings.cutBladeType || '--'}
                    </div>
                    <div className="text-sm text-gray-400">Blade Type</div>
                  </div>
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      {fabricSettings.cutSpeed ?? '--'}
                    </div>
                    <div className="text-sm text-gray-400">Speed</div>
                  </div>
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      {fabricSettings.cutPressure ?? '--'}
                    </div>
                    <div className="text-sm text-gray-400">Pressure</div>
                  </div>
                  <div className="bg-purple-900/30 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-400">
                      {fabricSettings.cutPasses}
                    </div>
                    <div className="text-sm text-gray-400">Passes</div>
                  </div>
                </div>
                {fabricSettings.cutNotes && (
                  <div className="mt-3 p-3 bg-purple-900/20 rounded-lg text-sm text-purple-200">
                    <strong>Notes:</strong> {fabricSettings.cutNotes}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Message if no fabric settings found but at fabric station */}
          {isFabricStation && !fabricSettings && workOrder?.screenMaterial && (
            <Card className="border-2 border-gray-600/50">
              <div className="text-center text-gray-400 py-4">
                <p>No weld/cut settings found for: <span className="text-yellow-400">{workOrder.screenMaterial}</span></p>
                <p className="text-sm mt-1">Contact admin to add settings for this fabric type.</p>
              </div>
            </Card>
          )}
        </>
      )}

      {activeTab === 'bom' && (
        <>
          {/* BOM Items Section */}
          <Card className="border-2 border-green-400/50">
            <h3 className="text-xl font-bold text-green-400 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              Parts for This Station
            </h3>
            <div className="text-sm text-gray-400 mb-4">
              Showing parts for: <span className="text-yellow-400 font-medium">{currentWorkstation.replace(/_/g, ' ').toUpperCase()}</span>
            </div>
            <div className="space-y-3">
              {bomItems.map((item) => (
                <div
                  key={item.id}
                  className={clsx(
                    'bg-gray-800 rounded-lg p-4 border-l-4',
                    item.consumed ? 'border-gray-600 opacity-60' : 'border-green-500'
                  )}
                >
                  <div className="flex gap-3">
                    {/* Part thumbnail */}
                    <div className="w-16 h-16 flex-shrink-0 bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.partNumber}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-500 text-[10px] text-center leading-tight px-1">{item.componentType.replace(/_/g, ' ')}</span>
                      )}
                    </div>
                    {/* Part info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <div className="min-w-0">
                          <div className="text-lg font-semibold text-white">{item.partNumber}</div>
                          <div className="text-sm text-gray-400 truncate">{item.description}</div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          {item.cutLength ? (
                            <div className="text-2xl font-bold text-yellow-400">{item.cutLength}"</div>
                          ) : (
                            <div className="text-2xl font-bold text-white">x{item.quantity}</div>
                          )}
                          <div className="text-xs text-gray-500">{item.unit}</div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1">
                        {item.allocated && !item.consumed && (
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">ALLOCATED</span>
                        )}
                        {item.consumed && (
                          <span className="px-2 py-1 bg-gray-600/20 text-gray-400 text-xs rounded">CONSUMED</span>
                        )}
                        <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded">
                          {item.componentType.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {bomItems.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No parts assigned to this workstation
                </div>
              )}
            </div>
          </Card>

          {/* Cut Sizes Summary */}
          {cutSheet && cutSheet.length > 0 && unitCutSheet.cutSizes && (
            <Card className="border-2 border-yellow-400/30 mt-4">
              <h4 className="text-lg font-bold text-yellow-400 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
                Cut Sizes for This Unit
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'fabricWidth', label: 'Fabric Width' },
                  { key: 'fabricHeight', label: 'Fabric Height' },
                  { key: 'housingMain', label: 'Housing Main' },
                  { key: 'housingCover', label: 'Housing Cover' },
                  { key: 'vTrack', label: 'V-Track' },
                  { key: 'rollerTube', label: 'Roller Tube' },
                  { key: 'weightBar', label: 'Weight Bar' },
                  { key: 'uChannel', label: 'U-Channel' },
                  { key: 'kederGuide', label: 'Keder Guide' },
                  { key: 'screwCover', label: 'Screw Cover' },
                ].filter(({ key }) => (unitCutSheet.cutSizes as any)[key] != null && (unitCutSheet.cutSizes as any)[key] > 0)
                  .map(({ key, label }) => (
                  <div key={key} className="flex justify-between bg-gray-800 rounded px-3 py-2">
                    <span className="text-gray-400 text-sm">{label}</span>
                    <span className="text-white font-mono font-bold">{(unitCutSheet.cutSizes as any)[key]}"</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {activeTab === 'details' && (
        <>
          {/* Current Step */}
          <Card className="border border-yellow-400/50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
              <span className="text-yellow-400 font-semibold">Current Step</span>
            </div>
            <div className="text-xl text-white font-medium">{workOrder.stepName}</div>
            {workOrder.startedAt && (
              <div className="text-sm text-gray-400 mt-1">
                Started {format(new Date(workOrder.startedAt), 'h:mm a')}
              </div>
            )}
          </Card>

          {/* Parts Order Detail View */}
          {(workOrder as any).productType === 'part' ? (
            <>
              <Card className="border-2 border-blue-400/50">
                <h3 className="text-lg font-semibold text-blue-400 mb-3">Parts Order Item</h3>
                <div className="space-y-2 text-sm">
                  {(() => {
                    let parsed: any = null;
                    try {
                      if (product.notes && typeof product.notes === 'string') {
                        parsed = JSON.parse(product.notes);
                      }
                    } catch { /* not JSON */ }
                    if (parsed && typeof parsed === 'object') {
                      return (
                        <>
                          {parsed.name && <DetailRow label="Part Name" value={String(parsed.name)} />}
                          {parsed.sku && <DetailRow label="SKU" value={String(parsed.sku)} />}
                          {parsed.color && <DetailRow label="Color" value={formatColor(String(parsed.color))} />}
                          {parsed.quantity && <DetailRow label="Quantity" value={`${parsed.quantity}${parsed.unit ? ` ${parsed.unit}` : ''}`} />}
                          {parsed.lengthInches && <DetailRow label="Length" value={`${parsed.lengthInches}"`} />}
                          {parsed.exactLength && <DetailRow label="Exact Length" value="Yes" highlight />}
                        </>
                      );
                    }
                    return product.notes ? (
                      <div className="text-white whitespace-pre-wrap">{product.notes}</div>
                    ) : (
                      <div className="text-gray-500">No part details available</div>
                    );
                  })()}
                </div>
              </Card>
              {product.motorType && (
                <Card>
                  <h3 className="text-lg font-semibold text-yellow-400 mb-3">Configuration</h3>
                  <div className="space-y-2 text-sm">
                    {product.motorType && <DetailRow label="Motor Type" value={formatDbValue(String(product.motorType))} />}
                    {product.motorSide && <DetailRow label="Motor Side" value={formatDbValue(String(product.motorSide))} />}
                    {product.boxSize && product.systemType !== 'open_roll' && <DetailRow label="Box Size" value={formatDbValue(String(product.boxSize))} />}
                  </div>
                </Card>
              )}
            </>
          ) : (
            <>
              {/* Production Dimensions — System Size & Box Size */}
              <Card>
                <h3 className="text-lg font-semibold text-yellow-400 mb-3">Production Dimensions</h3>
                <div className="space-y-3">
                  <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                    <div className="text-xs text-blue-400 uppercase tracking-wide mb-2">System Size</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Width</span>
                        <span className="text-white font-mono font-bold">{unitCutSheet?.systemWidth || workOrder.width}"</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Height</span>
                        <span className="text-white font-mono font-bold">{unitCutSheet?.systemHeight || workOrder.height}"</span>
                      </div>
                    </div>
                  </div>
                  {(product.systemType === 'open_roll') ? (
                    <div className="bg-amber-900/30 border border-amber-500/40 rounded-lg p-3">
                      <div className="text-xs text-amber-400 uppercase tracking-wide font-bold">Open Roll — No Housing Box</div>
                      <div className="text-gray-400 text-sm mt-1">Open roll unit: no box cut. Cut to system size above.</div>
                    </div>
                  ) : unitCutSheet?.calculatedDimensions && (() => {
                    const cd = unitCutSheet.calculatedDimensions!;
                    const boxW = (cd.widthSelected || 0) + (cd.boxWidthAdjustment || 0) - (cd.boxDeduction || 0) + (cd.dealerBoxAdjustment || 0);
                    return boxW > 0 ? (
                      <div className="bg-indigo-900/30 border border-indigo-500/30 rounded-lg p-3">
                        <div className="text-xs text-indigo-400 uppercase tracking-wide mb-2">Box Size</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex justify-between">
                            <span className="text-gray-400 text-sm">Width</span>
                            <span className="text-white font-mono font-bold">{boxW.toFixed(4).replace(/\.?0+$/, '')}"</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400 text-sm">Height</span>
                            <span className="text-white font-mono font-bold">{unitCutSheet?.systemHeight || workOrder.height}"</span>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </Card>

              {/* Fabric/Material Details */}
              <Card>
                <h3 className="text-lg font-semibold text-yellow-400 mb-3">Material Details</h3>
                <div className="space-y-2 text-sm">
                  <DetailRow label="Fabric Type" value={formatFabricType(workOrder.screenMaterial)} />
                  <DetailRow label="Fabric Color" value={formatColor(workOrder.screenColor)} />
                  <DetailRow label="Fabric Width" value={`${unitCutSheet?.cutSizes?.fabricWidth || workOrder.width}"`} />
                  <DetailRow label="Fabric Height" value={`${unitCutSheet?.cutSizes?.fabricHeight || workOrder.height}"`} />
                  <DetailRow label="Seam" value={product.seam ? 'Yes' : 'No'} highlight={!!product.seam} />
                  <DetailRow label="Biased Cut" value={product.biasedCut ? `Yes (${product.biasedCutInches || 0}"${product.biasedCutSide ? `, ${product.biasedCutSide === 'left' ? 'right' : 'left'} side shorter` : ''})` : 'No'} highlight={!!product.biasedCut} />
                  <DetailRow label="Angled Bottom" value={product.angledBottom ? 'Yes' : 'No'} highlight={!!product.angledBottom} />
                  <DetailRow label="Top Panel" value={String(product.topPanel || 'None')} />
                </div>
              </Card>

              {/* System Details */}
              <Card>
                <h3 className="text-lg font-semibold text-yellow-400 mb-3">System Details</h3>
                <div className="space-y-2 text-sm">
                  <DetailRow label="System Type" value={formatDbValue(String(product.systemType || 'box'))} />
                  <DetailRow label="Frame Color" value={formatColor(String(product.systemColor))} />
                  {(product as any).componentColors && Object.keys((product as any).componentColors).length > 0 && (() => {
                    const cc = (product as any).componentColors;
                    const fmt = (color: string, ralKey: string) => {
                      if (color === 'custom' && cc[ralKey]) return `Custom (${cc[ralKey]})`;
                      return color.charAt(0).toUpperCase() + color.slice(1);
                    };
                    return (
                      <div className="bg-orange-900 border-2 border-orange-400 rounded-lg p-3 my-2">
                        <div className="text-orange-300 text-xs font-bold mb-2">⚠️ MIXED COMPONENT COLORS</div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {cc.housingColor && <div><span className="text-orange-300">Housing:</span> <span className="text-white font-bold ml-1">{fmt(cc.housingColor, 'housingRalNumber')}</span></div>}
                          {cc.weightBarColor && <div><span className="text-orange-300">Weight Bar:</span> <span className="text-white font-bold ml-1">{fmt(cc.weightBarColor, 'weightBarRalNumber')}</span></div>}
                          {cc.leftTrackColor && <div><span className="text-orange-300">L.Track:</span> <span className="text-white font-bold ml-1">{fmt(cc.leftTrackColor, 'leftTrackRalNumber')}</span></div>}
                          {cc.rightTrackColor && <div><span className="text-orange-300">R.Track:</span> <span className="text-white font-bold ml-1">{fmt(cc.rightTrackColor, 'rightTrackRalNumber')}</span></div>}
                          {cc.leftUChannelColor && <div><span className="text-orange-300">L.U-Ch:</span> <span className="text-white font-bold ml-1">{fmt(cc.leftUChannelColor, 'leftUChannelRalNumber')}</span></div>}
                          {cc.rightUChannelColor && <div><span className="text-orange-300">R.U-Ch:</span> <span className="text-white font-bold ml-1">{fmt(cc.rightUChannelColor, 'rightUChannelRalNumber')}</span></div>}
                        </div>
                      </div>
                    );
                  })()}
                  {product.systemType === 'open_roll' ? (
                    <DetailRow label="System Type" value="Open Roll (no housing box)" highlight />
                  ) : (
                    <>
                      <DetailRow label="Box Size" value={formatDbValue(String(product.boxSize))} />
                      <DetailRow label="Box Color" value={formatColor(String(product.boxColor))} />
                    </>
                  )}
                  <DetailRow label="Installation Type" value={formatInstallationType(String(product.installType))} />
                  <DetailRow label="Recessed" value={product.recessed ? 'Yes (Pocket)' : 'No'} highlight={!!product.recessed} />
                  <DetailRow label="Keder Color" value={formatColor(String(product.kederColor))} />
                  {product.screwCovers && (
                    <DetailRow label="Screw Covers" value={`Yes (${product.screwCoverQty || 2})`} />
                  )}
                  {product.ralNumber && (
                    <DetailRow label="RAL Number" value={String(product.ralNumber)} highlight />
                  )}
                </div>
              </Card>

              {/* Track Details */}
              <Card>
                <h3 className="text-lg font-semibold text-yellow-400 mb-3">Track Details</h3>
                <div className="space-y-2 text-sm">
                  <DetailRow label="Track Mount" value={formatTrackMount(String(product.trackMount))} />
                  <DetailRow label="Track Color" value={formatColor(String(product.trackColor))} />
                  <DetailRow label="U-Channel" value={formatUChannel(String(product.uChannel))} />
                  {String(product.uChannel) !== 'none' && (
                    <>
                      <DetailRow label="U-Channel Color" value={formatColor(String(product.uChannelColor))} />
                      <DetailRow label="U-Channel Deductions" value={String(product.uChannelDeductions)} />
                    </>
                  )}
                  {/* Jamb/recessed tracks: holes are drilled but springs are
                      installed in the field, not at the bench. */}
                  {(String(product.trackMount).toLowerCase() === 'jamb' ||
                    String(product.installType || '').toLowerCase().includes('recessed')) &&
                   String(product.springs).toLowerCase().includes('track') ? (
                    <DetailRow
                      label="Springs"
                      value={`Field install — do NOT install at bench (${String(product.trackMount).toLowerCase() === 'jamb' ? 'jamb' : 'recessed'} mount)`}
                    />
                  ) : (
                    <DetailRow label="Springs" value={formatDbValue(String(product.springs))} />
                  )}
                  {Number(product.springQuantity) > 0 && (
                    <DetailRow
                      label="Spring Quantity"
                      value={
                        Number((product as any).springHolesPerTrack) > 0
                          ? `${product.springQuantity} springs (${(product as any).springHolesPerTrack} holes/track)`
                          : `${product.springQuantity} springs`
                      }
                      highlight
                    />
                  )}
                </div>
              </Card>

              {/* Weight Bar Details */}
              <Card>
                <h3 className="text-lg font-semibold text-yellow-400 mb-3">Weight Bar</h3>
                <div className="space-y-2 text-sm">
                  <DetailRow label="Size" value={formatDbValue(String(product.weightBarSize))} />
                  <DetailRow label="Color" value={formatColor(String(product.weightBarColor))} />
                  <DetailRow label="Brush Size" value={formatBrushSize(String(product.weightBarBrush))} />
                </div>
              </Card>

              {/* Window Panel Details */}
              {workOrder.windowPanel && (
                <Card className="border border-blue-500/50">
                  <h3 className="text-lg font-semibold text-blue-400 mb-3">Window Panel Details</h3>
                  <div className="space-y-2 text-sm">
                    <DetailRow label="Orientation" value={workOrder.windowOrientation || 'Horizontal'} />
                    <DetailRow label="Number of Windows" value={String(workOrder.windowCount || 1)} />
                    {workOrder.windowWidth && <DetailRow label="Window Width" value={`${workOrder.windowWidth}"`} />}
                    {workOrder.windowHeight && <DetailRow label="Window Height" value={`${workOrder.windowHeight}"`} />}
                    {workOrder.topBorderInches && <DetailRow label="Top Border" value={`${workOrder.topBorderInches}"`} />}
                    {workOrder.sideBorderInches && <DetailRow label="Side Borders" value={`${workOrder.sideBorderInches}"`} />}
                    {workOrder.bottomBorderInches && <DetailRow label="Bottom Border" value={`${workOrder.bottomBorderInches}"`} />}
                  </div>
                </Card>
              )}

              {/* Motor Details */}
              <Card>
                <h3 className="text-lg font-semibold text-yellow-400 mb-3">Motor & Controls</h3>
                <div className="space-y-2 text-sm">
                  <DetailRow label="Motor Type" value={formatDbValue(String(product.motorType))} />
                  <DetailRow label="Motor" value={formatDbValue(String(product.motor))} />
                  <DetailRow label="Motor Side" value={formatDbValue(String(product.motorSide))} />
                  {product.controls && <DetailRow label="Controls" value={String(product.controls)} />}
                  {product.accessories && <DetailRow label="Accessories" value={String(product.accessories)} />}
                  {/* Remote — always shown, "None" when qty=0 */}
                  {(() => {
                    const wo = workOrder as any;
                    const rQty = Number(wo.remoteQty) || 0;
                    const rType = String(wo.remoteType || '').toLowerCase();
                    const rAcc = wo.orderAccessories?.filter((a: any) => a.accessoryType === 'remote') || [];
                    if (rQty > 0 || (rType && rType !== 'none') || rAcc.length > 0) {
                      // Prefer accessory row (has the specific SKU-mapped type)
                      if (rAcc.length > 0) {
                        return rAcc.map((a: any, i: number) => (
                          <DetailRow key={`rem-${i}`} label="Remote"
                            value={`${formatDbValue(String(a.description || ''))}${a.quantity > 1 ? ` × ${a.quantity}` : ''}`} />
                        ));
                      }
                      return <DetailRow label="Remote" value={`${formatDbValue(rType)}${rQty > 1 ? ` × ${rQty}` : ''}`} />;
                    }
                    return <DetailRow label="Remote" value={<span className="text-gray-500 italic">None</span>} />;
                  })()}
                  {/* Wall Switch — always shown, "None" when qty=0 */}
                  {(() => {
                    const wo = workOrder as any;
                    const wQty = Number(wo.wallSwitchQty) || 0;
                    const wType = String(wo.wallSwitchType || '').toLowerCase();
                    const wAcc = wo.orderAccessories?.filter((a: any) => a.accessoryType === 'wall_switch') || [];
                    if (wQty > 0 || (wType && wType !== 'none') || wAcc.length > 0) {
                      if (wAcc.length > 0) {
                        return wAcc.map((a: any, i: number) => (
                          <DetailRow key={`sw-${i}`} label="Wall Switch"
                            value={`${formatDbValue(String(a.description || ''))}${a.quantity > 1 ? ` × ${a.quantity}` : ''}`} />
                        ));
                      }
                      return <DetailRow label="Wall Switch" value={`${formatDbValue(wType)}${wQty > 1 ? ` × ${wQty}` : ''}`} />;
                    }
                    return <DetailRow label="Wall Switch" value={<span className="text-gray-500 italic">None</span>} />;
                  })()}
                  {/* Motor upgrades (Somfy / Alpha) */}
                  {(workOrder as any).orderAccessories
                    ?.filter((a: any) => ['somfy_upgrade','alpha_upgrade','remote_upgrade'].includes(a.accessoryType))
                    .map((a: any, i: number) => (
                      <DetailRow key={`upg-${i}`} label="Motor Upgrade"
                        value={`${formatDbValue(String(a.description || ''))}${a.quantity > 1 ? ` × ${a.quantity}` : ''}`} />
                    ))}
                </div>
              </Card>

              {/* Notes — skip JSON (parts items store data as JSON in notes) */}
              {product.notes && !product.notes.startsWith('{') && (
                <Card>
                  <h3 className="text-lg font-semibold text-yellow-400 mb-2">Notes</h3>
                  <div className="text-white whitespace-pre-wrap">{product.notes}</div>
                </Card>
              )}

              {/* Installer Reference Sheet */}
              <Card className="border border-blue-500/50">
                <h3 className="text-lg font-semibold text-blue-400 mb-3">Installer Reference</h3>
                <p className="text-gray-400 text-sm mb-4">
                  View installation guide for this unit ({formatTrackMount(product.trackMount)}, {formatUChannel(product.uChannel)})
                </p>
                <div className="space-y-2">
                  <Button
                    variant="primary"
                    onClick={() => openAllInstallerSheets(workOrder.orderId)}
                    className="w-full touch-target"
                  >
                    <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print All Installer Sheets
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => openInstallerSheet(workOrder.orderId, workOrder.itemNumber)}
                    className="w-full touch-target"
                  >
                    <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Print This Unit Only
                  </Button>
                </div>
              </Card>
            </>
          )}

          {/* Step History */}
          <Card>
            <h3 className="text-lg font-semibold text-yellow-400 mb-3">Workflow Progress</h3>
            <div className="space-y-2">
              {workOrder.stepHistory.map((step, index) => (
                <div
                  key={index}
                  onClick={() => {
                    if (step.workstationId) {
                      openCard(step.workstationId, workOrder.id);
                    }
                  }}
                  className={clsx(
                    'flex items-center justify-between py-2 px-3 rounded-lg',
                    step.workstationId && 'cursor-pointer active:scale-[0.98] transition-transform',
                    step.status === 'completed' && 'bg-green-500/10',
                    step.status === 'in_progress' && 'bg-yellow-400/10 border border-yellow-400/50',
                    step.status === 'pending' && 'bg-gray-800',
                    step.status === 'skipped' && 'bg-gray-800 opacity-50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={clsx(
                      'w-3 h-3 rounded-full',
                      step.status === 'completed' && 'bg-green-500',
                      step.status === 'in_progress' && 'bg-yellow-400 animate-pulse',
                      step.status === 'pending' && 'bg-gray-600',
                      step.status === 'skipped' && 'bg-gray-500'
                    )} />
                    <span className={clsx(
                      'text-sm',
                      step.status === 'in_progress' ? 'text-yellow-400 font-medium' : 'text-gray-300'
                    )}>
                      {step.step}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {step.completedAt && (
                      <div className="text-right">
                        <span className="text-xs text-gray-500">
                          {format(new Date(step.completedAt), 'MMM d, h:mm a')}
                        </span>
                        {step.completedBy && (
                          <div className="text-xs text-gray-600">{step.completedBy}</div>
                        )}
                      </div>
                    )}
                    {step.workstationId && (
                      <svg className="w-4 h-4 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-4 no-print">
        <div className="flex gap-3 max-w-lg mx-auto">
          <Button variant="secondary" onClick={() => navigate(-1)} className="touch-target">
            Back
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const wsId = (workOrder as any)?.workstationId;
              if (wsId) {
                openCard(wsId, workOrder.id);
              } else {
                navigate('/work-cards');
              }
            }}
            className="flex-1 touch-target"
          >
            Work Card
          </Button>
          <Button
            variant="secondary"
            onClick={() => handlePrintLabel()}
            disabled={actionLoading === 'print'}
            className="touch-target"
          >
            {actionLoading === 'print' ? 'Printing...' : 'Print'}
          </Button>
          {workOrder.status === 'in_progress' && (
            <Button
              variant="success"
              onClick={handleComplete}
              disabled={actionLoading === 'complete' || actionLoading === 'drop' || actionLoading === 'calc'}
              className="flex-1 touch-target"
            >
              {actionLoading === 'calc' ? 'Checking...' : actionLoading === 'complete' ? 'Completing...' : actionLoading === 'drop' ? 'Saving...' : 'Complete'}
            </Button>
          )}
        </div>
      </div>

      {/* Drop Entry Modal */}
      {showDropModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-white mb-4">Enter Leftover Material</h2>
            {dropLength && parseFloat(dropLength) > 0 && dropPartType && (
              <div className="mb-3 px-3 py-2 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                <span className="text-blue-400 text-xs font-semibold">AUTO-CALCULATED FROM BOM</span>
                <span className="text-gray-400 text-xs ml-2">— verify and adjust if needed</span>
              </div>
            )}
            <p className="text-gray-400 text-sm mb-4">
              If you have a leftover piece from this cut, enter its length below.
              Pieces 84" or longer will be saved for future use.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Leftover Length (inches)</label>
                <input
                  type="number"
                  value={dropLength}
                  onChange={(e) => setDropLength(e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="e.g., 96"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-lg placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                  autoFocus
                />
                {dropLength && parseFloat(dropLength) > 0 && (
                  <div className={clsx(
                    'mt-2 text-sm font-medium',
                    parseFloat(dropLength) >= MIN_USABLE_DROP ? 'text-green-400' : 'text-orange-400'
                  )}>
                    {parseFloat(dropLength) >= MIN_USABLE_DROP
                      ? `Usable drop - will be saved to inventory`
                      : `Too short (min ${MIN_USABLE_DROP}") - will be logged as waste`}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Part Type</label>
                <select
                  value={dropPartType}
                  onChange={(e) => setDropPartType(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                >
                  <option value="">Select part type...</option>
                  <option value="roller_tube">Roller Tube</option>
                  <option value="housing_main">Housing Main</option>
                  <option value="housing_cover">Housing Cover</option>
                  <option value="v_track">V-Track</option>
                  <option value="u_channel">U-Channel</option>
                  <option value="weight_bar">Weight Bar</option>
                  <option value="keder_guide">Keder Guide</option>
                  <option value="fabric">Fabric</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Part Number (optional)</label>
                <input
                  type="text"
                  value={dropPartNumber}
                  onChange={(e) => setDropPartNumber(e.target.value.toUpperCase())}
                  placeholder="e.g., ROLLER-TUBE-3.75"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={dropNotes}
                  onChange={(e) => setDropNotes(e.target.value)}
                  placeholder="Any notes about this piece..."
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={handleSkipDrop}
                className="flex-1"
              >
                No Leftover
              </Button>
              <Button
                variant="success"
                onClick={handleCreateDrop}
                disabled={!dropLength || !dropPartType || actionLoading === 'drop'}
                className="flex-1"
              >
                {actionLoading === 'drop' ? 'Saving...' : 'Save Drop'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Label Type Picker Modal */}
      {showLabelPicker && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-sm p-5">
            <h3 className="text-lg font-semibold text-yellow-400 mb-4">Select Label Type</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { type: 'material', label: 'Fabric', icon: '🧵' },
                { type: 'track', label: 'Track', icon: '📏' },
                { type: 'housing', label: 'Housing', icon: '📦' },
                { type: 'roll_tube', label: 'Roll Tube', icon: '🔧' },
                { type: 'bottom_bar', label: 'Weight Bar', icon: '⚖️' },
                { type: 'final_product', label: 'Final Product', icon: '✅' },
                { type: 'shipping', label: 'Shipping', icon: '🚚' },
              ].map((opt) => (
                <button
                  key={opt.type}
                  onClick={() => handlePrintLabel(opt.type)}
                  className="flex flex-col items-center gap-1 p-4 bg-gray-800 hover:bg-yellow-400/20 border border-gray-700 hover:border-yellow-400 rounded-lg transition-colors"
                >
                  <span className="text-2xl">{opt.icon}</span>
                  <span className="text-sm text-gray-200 font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowLabelPicker(false)}
              className="w-full mt-4 py-2 text-gray-400 hover:text-white text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Drop Label Print Modal */}
      {showDropLabelModal && createdDrop && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-green-400 mb-4">Drop Saved!</h2>

            <div className="bg-gray-900 rounded-lg p-4 mb-4">
              <div className="text-center mb-3">
                <div className="text-2xl font-bold text-yellow-400">{createdDrop.dropId}</div>
                <div className="text-3xl font-bold text-white mt-2">{createdDrop.lengthInches}"</div>
                <div className="text-gray-400">{createdDrop.partType.replace(/_/g, ' ')}</div>
              </div>
              <div className="border-t border-gray-700 pt-3 mt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Part:</span>
                  <span className="text-white">{createdDrop.partNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date:</span>
                  <span className="text-white">{format(new Date(createdDrop.createdAt), 'MM/dd/yyyy h:mm a')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">From Order:</span>
                  <span className="text-white">{createdDrop.orderNumber || 'N/A'}</span>
                </div>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Print a label for this drop and attach it to the material.
            </p>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleAfterPrint}
                className="flex-1"
              >
                Skip Print
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  handlePrintDropLabel();
                  // Auto-advance after a short delay
                  setTimeout(handleAfterPrint, 1000);
                }}
                className="flex-1"
              >
                Print Label
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bin Location Scan Modal */}
      {showBinScanModal && createdDrop && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-white mb-4">Enter Bin Location</h2>
            <p className="text-gray-400 text-sm mb-4">
              Enter where you're storing this drop so others can find it easily.
            </p>

            <div className="bg-gray-900 rounded-lg p-3 mb-4">
              <div className="text-sm text-gray-400">Storing drop:</div>
              <div className="text-lg font-bold text-yellow-400">{createdDrop.dropId}</div>
              <div className="text-white">{createdDrop.lengthInches}" {createdDrop.partType.replace(/_/g, ' ')}</div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bin Location (Readable Name)</label>
                <input
                  type="text"
                  value={binLocation}
                  onChange={(e) => setBinLocation(e.target.value.toUpperCase())}
                  placeholder="e.g., RACK A SHELF 3 or BAY 5 SECTION 2"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-lg placeholder-gray-500 focus:outline-none focus:border-yellow-400"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">Enter in plain English so anyone can find it</p>
              </div>

              {/* Quick location buttons */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Quick Select</label>
                <div className="grid grid-cols-3 gap-2">
                  {['RACK A', 'RACK B', 'RACK C', 'BAY 1', 'BAY 2', 'BAY 3', 'SHELF 1', 'SHELF 2', 'FLOOR'].map((loc) => (
                    <button
                      key={loc}
                      onClick={() => setBinLocation(prev => prev ? `${prev} ${loc}` : loc)}
                      className="px-2 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded border border-gray-600"
                    >
                      {loc}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowBinScanModal(false);
                    finishWorkOrder();
                  }}
                  className="flex-1"
                >
                  Skip
                </Button>
                <Button
                  variant="success"
                  onClick={() => handleSaveBinLocation(false)}
                  disabled={!binLocation.trim() || actionLoading === 'bin'}
                  className="flex-1"
                >
                  {actionLoading === 'bin' ? 'Saving...' : 'Save'}
                </Button>
              </div>
              <Button
                variant="primary"
                onClick={() => handleSaveBinLocation(true)}
                disabled={!binLocation.trim() || actionLoading === 'bin'}
                className="w-full"
              >
                {actionLoading === 'bin' ? 'Saving...' : 'Save & Print Label'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Available Drops Modal */}
      {showAvailableDropsModal && availableDrops.length > 0 && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-2">Available Drops</h2>
            <p className="text-gray-400 text-sm mb-4">
              These leftover pieces are available. Using drops saves material!
            </p>

            <div className="space-y-3">
              {availableDrops.map((drop) => (
                <div
                  key={drop.id}
                  className="bg-gray-900 rounded-lg p-4 border border-gray-700 hover:border-yellow-400 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="text-yellow-400 font-bold text-sm">{drop.dropId}</div>
                      <div className="text-white text-2xl font-bold">{drop.lengthInches}"</div>
                      <div className="text-gray-400 text-sm">{drop.partType.replace(/_/g, ' ')}</div>
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleUseExistingDrop(drop)}
                      disabled={actionLoading === 'useDrop'}
                    >
                      Use This
                    </Button>
                  </div>

                  {/* Location - Large and Prominent */}
                  {drop.binLocation && (
                    <div className="mt-3 bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-3">
                      <div className="text-xs text-yellow-400 font-medium mb-1">FIND AT:</div>
                      <div className="text-lg font-bold text-white">{drop.binLocation}</div>
                    </div>
                  )}
                  {!drop.binLocation && (
                    <div className="mt-3 bg-gray-800 border border-gray-700 rounded-lg p-3">
                      <div className="text-xs text-gray-500 font-medium mb-1">LOCATION:</div>
                      <div className="text-sm text-gray-400">Not assigned - check with supervisor</div>
                    </div>
                  )}

                  <div className="text-xs text-gray-500 mt-2">
                    Created: {format(new Date(drop.createdAt), 'MMM d, yyyy')}
                    {drop.orderNumber && <span className="ml-2">From: {drop.orderNumber}</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-700">
              <Button
                variant="secondary"
                onClick={() => setShowAvailableDropsModal(false)}
                className="w-full"
              >
                Use New Stock Instead
              </Button>
            </div>
          </div>
        </div>
      )}

      <WorkCardModal
        open={cardModal.open}
        workstationId={cardModal.wsId}
        workOrderId={cardModal.woId}
        onClose={closeCard}
      />
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}

function DetailRow({ label, value, highlight }: DetailRowProps) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={clsx(
        'text-right',
        highlight ? 'text-red-400 font-medium' : 'text-white'
      )}>
        {value}
      </span>
    </div>
  );
}

interface CutRowProps {
  label: string;
  value: number;
  unit: string;
  highlight?: boolean;
  stockLength?: number;
  isExtrusion?: boolean;
}

function CutRow({ label, value, unit: _unit, highlight, stockLength, isExtrusion }: CutRowProps) {
  // Defensive guard — if a caller forgot to wrap an optional cut size in a
  // null check (e.g. housingMain on open-roll orders), don't crash the page.
  if (value == null || Number.isNaN(value)) return null;
  const fractionDisplay = toFraction(value);
  const decimalDisplay = value.toFixed(4).replace(/\.?0+$/, '');

  return (
    <div className={clsx(
      'flex justify-between items-center py-3 px-4 rounded-lg',
      highlight ? 'bg-yellow-400/10' : isExtrusion ? 'bg-blue-900/20' : 'bg-gray-800'
    )}>
      <div className="flex items-center gap-2">
        <span className="text-gray-300 text-lg">{label}</span>
        {isExtrusion && (
          <span className="px-1.5 py-0.5 bg-blue-500/30 text-blue-400 text-xs rounded">EXT</span>
        )}
      </div>
      <div className="text-right">
        <div className={clsx(
          'text-2xl font-bold font-mono',
          highlight ? 'text-yellow-400' : 'text-white'
        )}>
          {fractionDisplay}"
        </div>
        <div className="text-xs text-gray-500 font-mono">
          ({decimalDisplay}")
          {stockLength && (
            <span className="ml-2 text-blue-400">
              from {stockLength / 12}' stick
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
